import { useEffect, useState, type FC } from 'react'
import { useForm, Controller } from 'react-hook-form'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import {
  EnrollmentTaxFilingStatusType,
  MaritalStatus,
  useGetEnrollmentMultiFinancesData,
  useSubmitEnrollmentMultiFinancesData,
} from '@egw/api'
import { Input, Button, Alert, AlertType, GenericError, RadioGroup, ProgressBar } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { EnrollmentMultiLayout } from '@egw/layouts'
import { useRouter } from '@egw/router'
import { formatInputToCurrency, removeCurrencySymbols } from '@egw/utils'
import { sendEvent } from '@egw/events'
import { WEALTH_ROUTES } from '@egw/constants'
import { useFlags } from '@egw/feature-flags'
import { type FeatureFlags } from '@egw/constants'
import { PageHeader } from '../../../components'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { netWorthRangeItems } from '../../../constants/confirm-personal-info/confirm-personal-info'
import { EDataLayerEventWealth } from '../../../constants/events/EDataLayerEventWealth'
import { newPathIsDifferent } from '../../../utils/general/new-path-is-different'
import { ROUTES } from '../../../constants/routes/routes-generic'

const log = new Logger('Enrollment - Multi - Finances')

interface FormData {
  maritalStatus: MaritalStatus | undefined
  taxFilingStatus: EnrollmentTaxFilingStatusType | string
  income: string
  netWorth: string
}

export const FinancesPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const { navigate } = useRouter()
  const { flags } = useFlags<FeatureFlags>()

  const { data, isLoading, error } = useGetEnrollmentMultiFinancesData()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentMultiFinancesData()
  const [hasShownIntent, setHasShownIntent] = useState<boolean>(false)
  const [hasFormInitialized, setHasFormInitialized] = useState(false)

  const applicationId = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.applicationId : ''
  const progress = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.progress : undefined

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors: formErrors, isValid },
  } = useForm<FormData>({
    defaultValues: {
      maritalStatus: undefined,
      taxFilingStatus: '',
      income: '',
      netWorth: '',
    },
    mode: 'onChange',
  })

  const maritalStatus =
    data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.investmentProfile.maritalStatus : undefined
  const taxFilingStatus =
    data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.investmentProfile.taxFilingStatus : ''
  const personalAnnualGrossIncome =
    data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.personalAnnualGrossIncome : ''
  const personalNetWorthRange =
    data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.personalNetWorthRange : ''

  useEffect(() => {
    if (data?.__typename === 'SubmitEnrollmentSuccess' && data.enrollmentData && !hasFormInitialized) {
      reset({
        maritalStatus: maritalStatus || undefined,
        taxFilingStatus: taxFilingStatus || '',
        income: formatInputToCurrency(personalAnnualGrossIncome?.toString() ?? '', true),
        netWorth: personalNetWorthRange || '',
      })
      setHasFormInitialized(true)
    }
  }, [data, reset, hasFormInitialized])

  const income = watch('income')

  // Protect route
  useValidateRoute(data?.path)

  useEffect(() => {
    if (!hasShownIntent && income) {
      setHasShownIntent(true)
      sendEvent(EDataLayerEventWealth.BANKHYCHYS_FINANCES_INTENT, {
        applicationId,
        annualIncome: income,
      })
    }
  }, [applicationId, hasShownIntent, income])

  const onSubmit = handleSubmit(async (fData: unknown): Promise<void> => {
    const formData = fData as FormData
    if (isValid) {
      try {
        const response = await mutate({
          investmentProfile: {
            maritalStatus: formData.maritalStatus,
            taxFilingStatus: formData.taxFilingStatus as EnrollmentTaxFilingStatusType,
          },
          personalAnnualGrossIncome: removeCurrencySymbols(formData.income.toString()) || 0,
          personalNetWorthRange: formData.netWorth,
        })
        sendEvent(EDataLayerEventWealth.BANKHYCHYS_FINANCES_SUBMIT, {
          applicationId,
          annualIncome: formData.income,
        })
        // If path exists and is different
        if (response?.path && newPathIsDifferent(location.pathname, response.path)) {
          sendEvent(EDataLayerEventWealth.BANKHYCHYS_FINANCES_VERIFIED, {
            applicationId,
            annualIncome: formData.income,
          })
          navigate(response.path)
        } else {
          // If no path from BE, redirect to error
          if (!response?.path) {
            navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ROUTES.ERROR.path}`)
          }
        }
      } catch (e: unknown) {
        log.error('Error submitting form', e)
      }
    }
  })

  // Determine which description to use based on feature flag
  const descriptionKey = flags.rolloutJointEnrollmentV1
    ? 'multi.finances.description_joint_rollout'
    : 'multi.finances.description'

  return (
    <EnrollmentMultiLayout title={t('multi.finances.page_title')}>
      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? (
        <div className="w-full">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error ? (
        <form onSubmit={(e) => void onSubmit(e)} className="w-full flex-1 flex flex-col justify-start">
          <PageHeader title={t('multi.finances.title')} className="mb-sm" />
          <ProgressBar showAnimation progressPercentage={progress} className="mb-md" />
          <p className="typo-primary-base text-text-meta mb-xl">{t(descriptionKey)}</p>

          <p className="typo-primary-base-semibold text-text-header mb-sm">{t('edit_fields.marital_status_title')}</p>
          <Controller
            name="maritalStatus"
            control={control}
            rules={{
              required: true,
            }}
            render={({ field: { ref: _, ...restField } }) => (
              <RadioGroup
                {...restField}
                options={Object.values(MaritalStatus).map((item) => ({
                  value: item,
                  label: t(`marital_status.${item}`),
                }))}
                name="maritalStatus"
                className="pb-xs typo-primary-base mt-xs mb-xl"
                testId="marital-status-radio"
              />
            )}
          />

          <p className="typo-primary-base-semibold text-text-header mb-sm">
            {t('multi.finances.tax_filing_status_title')}
          </p>
          <Controller
            name="taxFilingStatus"
            control={control}
            rules={{
              required: true,
            }}
            render={({ field: { ref: _, ...restField } }) => (
              <RadioGroup
                {...restField}
                options={Object.values(EnrollmentTaxFilingStatusType).map((item) => ({
                  value: item,
                  label: t(`investments.filing-status.tax_filing_status.${item}`),
                }))}
                name="taxFilingStatus"
                className="pb-xs typo-primary-base mt-xs mb-xl"
                testId="tax-filing-status-radio"
              />
            )}
          />
          <p className="typo-primary-base-semibold text-text-header mb-sm">{t('multi.finances.annual_income_title')}</p>
          <p className="typo-primary-base-regular text-text-meta mb-sm">{t('multi.finances.annual_income_p1')}</p>
          <p className="typo-primary-base-regular text-text-meta mb-sm">{t('multi.finances.annual_income_p2')}</p>
          <p className="typo-primary-base-regular text-text-meta mb-md">{t('multi.finances.annual_income_p3')}</p>

          <Controller
            name="income"
            control={control}
            rules={{
              required: t('common.required_field'),
            }}
            render={({ field }) => (
              <Input
                {...field}
                onChange={(e) => {
                  const { value } = e.target
                  let formattedValue =
                    formatInputToCurrency(value, true) === '0' ? '' : formatInputToCurrency(value, true) // format to currency
                  formattedValue = formattedValue.replaceAll('.', '') // remove cents
                  e.target.value = formattedValue // set internal value
                  field.onChange(e)
                  setValue('income', formattedValue)
                }}
                label={t('multi.finances.annual_income')}
                testId="annual-income-input"
                error={formErrors.income?.message}
                maxLength={12}
                isPII
              />
            )}
          />

          <p className="typo-primary-base-semibold text-text-header mt-xl mb-sm">
            {t('multi.finances.your_net_worth')}
          </p>

          <Controller
            name="netWorth"
            control={control}
            rules={{
              required: t('common.required_field'),
            }}
            render={({ field: { ref: _, ...restField } }) => (
              <RadioGroup
                {...restField}
                onChange={(e) => {
                  restField.onChange(e)
                }}
                options={netWorthRangeItems.map((item) => ({
                  value: item.value,
                  label: t(`multi.finances.personal_net_worth_range.${item.value}`),
                }))}
                name="personalNetWorthRange"
                className="pt-xs pb-xs"
                testId="net-worth-radio"
                isPII
              />
            )}
          />

          <div className="flex-1 md:hidden" />

          {submitError ? <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" /> : null}

          {/* AUTO GENERATED COMPONENTS */}

          <Button
            type="submit"
            className="w-full md:w-min mt-xl"
            isDisabled={!isValid}
            isLoading={isSubmitting}
            testId="primaryAction_button"
          >
            {t('common.continue')}
          </Button>
        </form>
      ) : null}

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {error ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentMultiLayout>
  )
}
